import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/LocaleContext';
import { COUNTRIES, REGIONS, getLanguage } from '../i18n/countries';

export function CountryPicker() {
  const { t } = useTranslation();
  const { country, availableLanguages, setCountry, setLanguage, language } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Filter countries by search query
  const filteredRegions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return REGIONS.map((region) => ({
        region,
        countries: COUNTRIES.filter((c) => c.region === region),
      }));
    }
    return REGIONS.map((region) => ({
      region,
      countries: COUNTRIES.filter(
        (c) =>
          c.region === region &&
          (c.name.toLowerCase().includes(query) ||
            c.code.toLowerCase().includes(query))
      ),
    })).filter((r) => r.countries.length > 0);
  }, [search]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-spi-parchment hover:border-spi-green/30 hover:bg-spi-green/5 transition-colors min-h-[44px]"
        aria-label={t('countryPicker.selectCountry')}
      >
        <span className="text-lg leading-none">{country.flag}</span>
        <span className="hidden sm:inline text-sm font-medium text-spi-charcoal">
          {country.code}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-spi-slate transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-spi-parchment z-50">
          {/* Sticky header with search */}
          <div className="sticky top-0 bg-white px-4 py-3 border-b border-spi-parchment z-10">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-spi-slate pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('countryPicker.search')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-spi-parchment rounded-lg bg-spi-cream/30 text-spi-charcoal placeholder:text-spi-slate/60 focus:ring-2 focus:ring-spi-green/30 focus:border-spi-green outline-none"
              />
            </div>
          </div>

          {filteredRegions.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-spi-slate">
              {t('countryPicker.noResults')}
            </div>
          ) : (
            filteredRegions.map(({ region, countries: regionCountries }) => (
              <div key={region}>
                <div className="px-4 py-1.5 text-[10px] font-semibold text-spi-slate uppercase tracking-wider bg-spi-cream/50">
                  {region}
                </div>
                {regionCountries.map((c) => {
                  const isSelected = c.code === country.code;
                  return (
                    <button
                      key={c.code}
                      onClick={() => {
                        setCountry(c.code);
                        if (c.languages.length <= 1) {
                          setIsOpen(false);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 min-h-[44px] text-left transition-colors ${
                        isSelected
                          ? 'bg-spi-green/10 text-spi-green-dark'
                          : 'hover:bg-spi-cream/50 text-spi-charcoal'
                      }`}
                    >
                      <span className="text-lg leading-none">{c.flag}</span>
                      <span className="flex-1 text-sm font-medium">{c.name}</span>
                      {isSelected && (
                        <svg className="w-4 h-4 text-spi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}

          {/* Language sub-selector */}
          {availableLanguages.length > 1 && (
            <div className="sticky bottom-0 bg-white border-t border-spi-parchment px-4 py-3">
              <div className="text-[10px] font-semibold text-spi-slate uppercase tracking-wider mb-2">
                {t('countryPicker.language')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableLanguages.map((lang) => {
                  const isActive = lang.code === language.code;
                  const langConfig = getLanguage(lang.code);
                  return (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsOpen(false);
                      }}
                      className={`px-3 py-1.5 min-h-[36px] rounded-lg text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-spi-green text-white'
                          : 'bg-spi-cream text-spi-charcoal hover:bg-spi-green/10'
                      }`}
                    >
                      {lang.nativeName}
                      {langConfig && !langConfig.isComplete && (
                        <span className="ml-1 text-[9px] opacity-70">({t('countryPicker.beta')})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
